// routing file for the app
import { SplashScreen } from '@capacitor/splash-screen'
import { IonApp, IonRouterOutlet } from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import loadable from '@loadable/component'
import flatten from 'flat'
import React, { useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { IntlProvider } from 'react-intl'
import { Route, Switch } from 'react-router-dom'
import { useBase } from '../context/BaseContext'
import { useAppDispatch, useAppSelector } from '../hooks/store'
import { messages, validLocale, isValidLocale } from '../locale'
import userStorage from '../storage/user.storage'
import { RootState } from '../store'
import { updateLanguage } from '../store/features/i18n/i18n.slice'
import { createStore } from '../utils/IonicStorage'
import AppUrlListener from './components/AppUrlListener/AppUrlListener'
import LangModal from './components/LangSwitcher/LangModal'
import { SANDBOX_PATH } from '../sandbox/sandbox'
const TabsBar = loadable(() => import('./components/TabsBar/TabsBar'))
const LandingPage = loadable(() => import('./pages/LandingPage/LandingPage'))
const EventPreview = loadable(() => import('./pages/EventPreview/EventPreview'))
const SSOCallback = loadable(() => import('./pages/SSOCallback/SSOCallback'))
const SandboxPage = loadable(() => import('../sandbox/SandboxPage'))

// ionic react router
const AppRouting: React.FC = () => {
    const dispatch = useAppDispatch()
    const { initFromStorage, setLang, lang } = useBase()
    const onUpdateLanguage = (language: string) => dispatch(updateLanguage(language))

    /**
     *  function to lock screen orientation
     */
    useEffect(() => {
        SplashScreen.hide()

        // setup store using localforage
        const setupStore = async () => {
            const updateLang = (lang: string) => {
                if (isValidLocale(lang)) {
                    userStorage.setLang(lang)
                    setLang(lang)
                    onUpdateLanguage(lang)
                }
            }

            await createStore('LuckysitDB')
            await initFromStorage()

            const getBrowserLanguage = () => {
                const splitLanguage = navigator.language.split('-')[0]
                return splitLanguage && isValidLocale(splitLanguage) ? splitLanguage : 'fr'
            }

            // Get language from store, if it's not there, get the browser language
            // If it's not a supported language, change it to french
            const language = await userStorage
                .getLang()
                .then((fetchedLanguage: string) => {
                    if (!fetchedLanguage) fetchedLanguage = getBrowserLanguage()
                    return isValidLocale(fetchedLanguage) ? fetchedLanguage : 'fr'
                })
                .catch(() => {
                    return getBrowserLanguage()
                })

            updateLang(language)
        }
        setupStore()
    }, [])

    // set screen orientation

    return (
        <HelmetProvider>
            <IntlProvider locale={lang} defaultLocale='fr' messages={flatten(messages[lang])}>
                <IonApp>
                    <>
                        <IonReactRouter>
                            <AppUrlListener />
                            <Switch>
                                {/* Routes SSO - Priorité pour éviter capture par TabsBar */}
                                <Route path="/sso-callback" exact component={SSOCallback} />
                                
                                {/* Routes Landing Page */}
                                <Route
                                    path={[
                                        `/${messages[lang].LANDING_PAGE.META.URL}`,
                                        `/${messages[lang].LANDING_PAGE.META.URL}/:slug/:placeId/:areaId`,
                                        `/${messages[lang].LANDING_PAGE.META.URLV2}`,
                                        `/${messages[lang].LANDING_PAGE.META.URLV2}/:slug/:placeId/:areaId`
                                    ]}
                                    exact
                                    component={() => <LandingPage currentPage='LANDING_PAGE' />}
                                />
                                
                                {/* L'aperçu des jeux du CMS, en bac à sable (roadmap/16) */}
                                <Route path={SANDBOX_PATH} component={SandboxPage} />

                                {/* URL dédiée par event + zone (test/preview signé) */}
                                <Route path={["/e/:eventSlug/:areaId", "/e/:eventSlug"]} exact component={EventPreview} />

                                {/* Routes principales avec TabsBar */}
                                <Route path="/" component={TabsBar} />
                            </Switch>
                        </IonReactRouter>
                    </>
                    <LangModal />
                </IonApp>
            </IntlProvider>
        </HelmetProvider>
    )
}

export default AppRouting
